import React, { useState, useEffect, useCallback } from 'react'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux'
import { Snackbar, List, ListItem, ListItemText, Button } from '@material-ui/core'
import { Flex, Box } from 'reflexbox'
import set from 'set-value'
import KeyIcon from '@material-ui/icons/VpnKey';
import cloneDeep from 'lodash.clonedeep';

import { HeaderContainer, BounceAnimatedContainer, PaperContainer, HorizontalRule, ContainerRight } from '../../healtheraComponents'
import { AddProceedCancelDialogButtons, Search, SearchBox, SearchIconStyled, CloseIconStyled, ListContainer, VerticalFlex } from './components/BranchComponents'
import DynamicForm from '../DynamicForm/DynamicForm'
import { Schema } from './InputSchema'
import TeamMembers from '../TeamMembers/TeamMembersContainer'
import HealtheraFooter from '../HealtheraFooter/HealtheraFooter'
import DynamicDialog from '../DynamicDialog/DynamicDialog'
import BranchLinkDialog from './components/BranchLinkDialog'
import NotificationPopup from '../NotificationPopup';
import DeliveryPartnerSettings from './DeliveryPartnerSettings/DeliveryPartnerSettings';
import IntegrationSettings from './components/IntegrationSettings';
import ECommerceSettings from './components/ECommerceSettings';
import PaidDeliverySettings from './PaidDeliverySettings/PaidDeliverySettings';
import PostalDeliverySettings from './components/PostalDeliverySettings';
import RxCommissionsSettings from './components/RxCommissionsSettings';
import MassInviteCsvDetails from './MassInviteCsvDetails/MassInviteCsvDetails'
import PaymentMethodSettings from './PaymentMethods/PaymentMethodsContainer';
import DeliveryOptionsTable from './components/DeliveryOptionsTable';
import ThirdPartyIntegrationSettings from './ThirdPartyIntegrations/ThirdPartyIntegrationSettings';
import AlphegaMemberSettings from './AlphegaMemberSettings/AlphegaMemberSettings';
import PharmacyDirectory from './PharmacyDirectory';

import * as branchActions from '../../redux/branches';
import * as orgActions from '../../redux/organisations';

import { validated } from '../../helpers';
import { getGlobalCommissionRates } from '../../redux/configurations';
import { useMutation, useQuery } from '@tanstack/react-query'
import masterServicesAPI from '../../apis/master-services'
import NationalServiceSettings from './NationalServicesSettings/NationalServiceSettings'
import errorRoutine from '../../utils/errorRoutine'
import styled from 'styled-components'

const BranchDetails = props => {
  const dispatch = useDispatch();

  const [branchDetails, setBranchDetails] = useState(null);
  const [validationArray, setValidationArray] = useState([])
  const [schema, setSchema] = useState({})
  const [toggleOrgDialog, setToggleOrgDialog] = useState(false)
  const [addOrgDialogCallback, setAddOrgDialogCallback] = useState(() => {})
  const [successNotificationFlag, setSuccessNotificationFlag] = useState(false);
  const [successNotificationMessage, setSuccessNotificationMessage] = useState(false);

  const branchID = props.match.params.branchID;
  const { selectedBranch: branch, error } = useSelector(state => state.branches)
  const { searchResults, organisation } = useSelector(state => state.organisations)
  const { globalCommissionRates } = useSelector(state => state.configurations)
  const { chain_id: orgID, integrations = {} } = branch || {};

  const { data: nationalMasterServices, refetch: refetchNationalServices, error: nationalServicesError } = useQuery({
    queryFn: () => masterServicesAPI.getNationalMasterServices(branchID),
    queryKey: [branchID],
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: postPharmacyToMasterService } = useMutation({
    mutationFn: ([serviceCatalogId, action]) => 
        masterServicesAPI.postPharmaciesToMasterService([
          {
            pharmacy_id: branchID,
            service_catalog_id: serviceCatalogId,
            action,
          }
        ]
    ),
  });

  const {
    national_master_services: availableNationalMasterServices,
    is_service_setup_by_master_id: pharmacyHasMasterServiceSetupByMasterId,
    is_pharmacy_included_by_master_id: pharmacyIncludedInMasterServiceByMasterId
  } = nationalMasterServices || {};

  useEffect(() => {
    dispatch(getGlobalCommissionRates());
  }, [dispatch]); 

  useEffect(() => {
    dispatch(branchActions.getBranch(branchID));
  }, [dispatch, branchID])

  useEffect(() => {
    if (branch) {
      setBranchDetails(branch)
      setSchema(Schema(branch))
    }
  }, [branch])

  useEffect(() => {
    if (orgID || (branch && branch.organisationID)) dispatch(orgActions.getOrganisation(orgID || branch.organisationID))
  }, [dispatch, orgID, branch])

  const onChange = async (params) => {
    const { field, validation = true, update, formatFunc } = params;
    let { value } = params;

    if (formatFunc && typeof formatFunc === 'function') {
      value = formatFunc(value);
    }

    set(validationArray, field, validation);
    set(branchDetails, field, value);
    setValidationArray(validationArray);

    const newBranchDetails = {
      ...branchDetails,
      postcode: branchDetails.address.postcode,
      active: (branchDetails.status !== 'inactive'),
      pharmacy_status: branchDetails.status
    };
    setBranchDetails(newBranchDetails);

    if (update) {
      const updateResponse = await dispatch(branchActions.updateBranch(branchID, newBranchDetails));
      await dispatch(branchActions.getBranch(branchID));
      const { payload: { data } = {} } = updateResponse || {};

      if (data && data.isCommissionStatusUpdated) {
        triggerSuccessNotification(
          'All patients who joined the pharmacy before Go Live Date have been switched to No Commission.'
        );
      }
    }
  };

  const handleClose = () => {
    dispatch(branchActions.resetBranchError())
  }

  const handleSubmit = async () => {
    await dispatch(branchActions.prepareBranch(branchDetails, branchID))
  }

  const generateLink = useCallback(() => {
    dispatch(branchActions.generateLink(branch.pharmacy_id))
  }, [dispatch, branch])

  const handleSearch = useCallback(({ target: { value } = {} } = {}) => {
    if (value && value.length > 2) dispatch(orgActions.search({ searchTerm: value, isActive: true, searchBy: 'orgs'}))
    else dispatch(orgActions.resetSearchResults())
  }, [dispatch])

  const handleChangeOrg = useCallback(orgId => {
    setToggleOrgDialog(true)
    setAddOrgDialogCallback(() => () => {
      setToggleOrgDialog(false)
      setBranchDetails({ ...branchDetails, organisationID: orgId, chain_id: orgId })
      dispatch(branchActions.updateBranch(branchID, { organisationID: orgId, chain_id: orgId }))
    })
  }, [dispatch, branchDetails, branchID])

  const closeSuccessNotification = () => {
    setSuccessNotificationFlag(false);
    setSuccessNotificationMessage('');
  };

  const triggerSuccessNotification = (message) => {
    setSuccessNotificationMessage(message);
    setSuccessNotificationFlag(true);
  };

  const valid = !validated(validationArray)

  const handleFieldChange = async (field, value) => {
    if (branch) {
      // Get the first field of fields chain (field1.field2.field3 -> field1);
      const [shallowField] = field.split('.');
      
      const branchUpdate = {
        [shallowField]: cloneDeep(branch[shallowField])
      };

      set(branchUpdate, field, value);
      await dispatch(branchActions.updateBranch(branch._id, branchUpdate));
    }
  }

  const handleNationalServiceSelect = async (masterServiceId, action) => {
    try {
      await postPharmacyToMasterService([masterServiceId, action]);
      await refetchNationalServices();
      triggerSuccessNotification(action === 'remove' ? 'Pharmacy removed from service' : 'Pharmacy added to service');
    } catch (error) {
      errorRoutine(error, triggerSuccessNotification);
    }
  }

  return (
    <>
      <NotificationPopup
        isOpen={successNotificationFlag}
        onClose={closeSuccessNotification}
        message={successNotificationMessage}
      />
      <Snackbar open={!!error}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        message={error?.data || error?.message || error}
      />
      {branch && <BounceAnimatedContainer>
        <PaperContainer $margintop={26} elevation={0}>
          <HeaderContainer marginright={`0px`} padding={`0 0 20px 0`}>
            <DynamicForm
              title={branch.pharmacy_name}
              save={handleSubmit}
              enclosed
              editable
              displayButtons
              displayDeleteButton={false}
              disabled={valid}
              margintop={`5px`}
              onChange={onChange}
              fields={schema.name} />

            <ContainerRight align='center'>
              <Box pr={2}>{branch.status === 'premium' ? 'Active' : 'Inactive'}</Box>
            </ContainerRight>
          </HeaderContainer>
          <Flex justify="space-between" style={{flexWrap:'wrap'}}>
            <VerticalFlex w="49%">
              <Box pt={2} pr={4} w={`100%`}>
                {!(branch.organisationID || branch.chain_id) && <>
                  <SearchBox>
                    <Search placeholder='Add to organisation' onChange={handleSearch} submit />
                    {searchResults ? <CloseIconStyled nativeColor={'#333'}  onClick={handleSearch} /> : <SearchIconStyled nativeColor={'#333'} onClick={handleSearch} />}
                  </SearchBox>
                  {searchResults && <ListContainer>
                    <List subheader={<li />}>
                      {searchResults.orgs.map(org =><ListItem key={org._id} button onClick={() => handleChangeOrg(org._id)}>
                        <ListItemText primary={org.name}/>
                      </ListItem>)}
                    </List>
                  </ListContainer>}
                </>}
                <h3>
                  {`Organisation: `}
                  {((branch.organisationID || branch.chain_id) && organisation)
                    ? <Link to={`/organisations/${organisation._id}`}>{organisation.name}</Link>
                    : 'Loading...'
                  }
                  {!(branch.organisationID || branch.chain_id) && 'None'}
                </h3>
                <HorizontalRule />
                <SectionContainer>
                  <DynamicForm
                    title={'Branch Address'}
                    save={handleSubmit}
                    editable
                    displayButtons
                    disabled={valid}
                    margintop={`5px`}
                    onChange={onChange}
                    fields={schema.address}
                  />
                  <DynamicForm
                    title={'Main Contact Person'}
                    save={handleSubmit}
                    editable
                    displayButtons
                    disabled={valid}
                    margintop={`5px`}
                    defaultValue={schema.pharmacyContact}
                    onChange={onChange}
                    fields={schema.pharmacyContact} />
                </SectionContainer>
              </Box>
              <HorizontalRule />
              <SectionContainer>
                <PharmacyDirectory pharmacy={branch} onChange={handleFieldChange} />
              </SectionContainer>
              <HorizontalRule />
              <SectionContainer>
                <DynamicForm
                  title="General Information"
                  save={handleSubmit}
                  editable
                  displayButtons
                  disabled={valid}
                  margintop={`15px`}
                  defaultValue={schema.generalInfo}
                  onChange={onChange}
                  fields={schema.generalInfo} />
                <BranchLinkDialog button={<Button style={{marginTop: '20px'}} onClick={generateLink}>Generate promotional link <KeyIcon /></Button>}/>
                <TeamMembers getMembers members={branch.teamMembers} branchID={branchID} />
              </SectionContainer>

            <ThirdPartyIntegrationSettings integrations={integrations} pharmacyId={branchID} />
            <AlphegaMemberSettings pharmacyId={branchID} />
            </VerticalFlex>
            <VerticalFlex w="49%">
              <IntegrationSettings pharmacy={branch} onChange={handleFieldChange} />
              <DeliveryPartnerSettings pharmacy={branch} onChange={handleFieldChange} onValidationError={triggerSuccessNotification} />
              <PaidDeliverySettings pharmacy={branch} organisation={organisation} onChange={handleFieldChange} onValidationError={triggerSuccessNotification} />
              <ECommerceSettings pharmacy={branch} organisation={organisation} onChange={handleFieldChange} onValidationError={triggerSuccessNotification} />
              <PostalDeliverySettings pharmacy={branch} onChange={handleFieldChange} />
              <NationalServiceSettings
                availableNationalMasterServices={availableNationalMasterServices}
                pharmacyHasMasterServiceSetupByMasterId={pharmacyHasMasterServiceSetupByMasterId}
                pharmacyIncludedInMasterServiceByMasterId={pharmacyIncludedInMasterServiceByMasterId}
                error={nationalServicesError?.response?.data?.error?.text}
                onChange={handleNationalServiceSelect}
              />
              <RxCommissionsSettings pharmacy={branch} organisation={organisation} globalCommissionRates={globalCommissionRates} onChange={handleFieldChange} onValidationError={triggerSuccessNotification} />
              <PaymentMethodSettings pharmacy={branch} organisation={organisation} onChange={handleFieldChange} />
              <MassInviteCsvDetails pharmacy={branch} />
            </VerticalFlex>
            <Box style={{width:'100%',marginTop:'1rem'}}>
              <DeliveryOptionsTable pharmacy={branch} onChange={handleFieldChange} />
            </Box>
          </Flex>
        </PaperContainer>
        {(orgID || (branch && branch.organisationID)) && <HealtheraFooter />}
      </BounceAnimatedContainer>}
      <DynamicDialog
        open={toggleOrgDialog} title={'Add to Organisation'}
        contentText={`Are you sure you want to add branch to this organisation`}
        actions={AddProceedCancelDialogButtons(addOrgDialogCallback, () => setToggleOrgDialog(false))}
      />
    </>
  )
};

export default withRouter(BranchDetails);

const SectionContainer = styled.div`
padding: 10px 0;
`;
